import React from "react";
import { Grid, Card, CardContent, Typography, Box, Chip } from "@mui/material";
import StarIcon from "@mui/icons-material/Stars";
import CrossIcon from "@mui/icons-material/Cancel";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ImgKeepGoing from "../../assets/images/motivation/1-KeepGoing.png";
import ImgYouGotThis from "../../assets/images/motivation/2-YouGotThis.png";
import ImgGreatWork from "../../assets/images/motivation/3-GreatWork.png";
import ImgAmazing from "../../assets/images/motivation/4-Amazing.png";
import {
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LabelList,
  Legend,
  CartesianGrid,
  Line,
  LineChart,
} from "recharts";
import { InfoTooltip } from "../InfoTooltip";
import {
  IBenchmarks,
  InsightByStandard,
  IVerifiedScore,
} from "../../types/insights";
import { StandardsProgressGrid } from "../../scenes/ActionPlanHomePage";
import { MaturityGraph, maturityGraphStages } from "../MaturityGraph";
import maturityList from "../MaturityGraph/maturity.json";
import {
  cultureValuesIcon,
  flexibleWorkIcon,
  hiringIcon,
  leadershipIcon,
  parentalLeaveIcon,
  performanceManagementIcon,
  policiesIcon,
  respectWorkIcon,
  sltExecIcon,
  transparentPayIcon,
} from "../../assets/icons";
import { Paths } from "../../types/app";
import { QuickLinksVertical } from "../QuickLinks";
import { formatMonthYearAU } from "../../utils/date";
import { Benchmarking } from "../Benchmarking";
import { chartTooltipStyles, ProgressOverTimeTooltip } from "../ChartTooltip";

const PIE_CHART_COLORS = ["#fde5b5", "#e0e0e0"];
const PIE_CHART_COLORS_SUCCESS = ["#d1eebb", "#e0e0e0"];

const getPieChartColours = (score: number) => {
  switch (true) {
    case score >= 70:
      return PIE_CHART_COLORS_SUCCESS;
    default:
      return PIE_CHART_COLORS;
  }
};

type StandardsGraphProps = {
  standardInsights: InsightByStandard[];
  overallScore: number;
  benchmarks: IBenchmarks;
  verifiedScores: IVerifiedScore;
};

// A single time period row in the "Recent Changes" card, showing the verified
// score change and the number of assessments completed within that window.
// TODO: re-enable `itemsCompleted` (commented below) once the backend supports
// proper Items Completed reporting.
const RecentChangePeriod = ({
  label,
  scoreChange,
  // itemsCompleted,
}: {
  label: string;
  scoreChange: number | null;
  // itemsCompleted: number;
}): React.ReactElement => {
  const changeColor =
    scoreChange === null || scoreChange === 0
      ? "text.primary"
      : scoreChange > 0
        ? "#1D9E75"
        : "#d32f2f";

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 4 }}>
        {/* Score Change */}
        <Box>
          <Typography variant="caption" color="text.secondary">
            Score Change
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {scoreChange !== null && scoreChange > 0 && (
              <ArrowUpwardIcon sx={{ fontSize: 20, color: "#1D9E75" }} />
            )}
            {scoreChange !== null && scoreChange < 0 && (
              <ArrowDownwardIcon sx={{ fontSize: 20, color: "#d32f2f" }} />
            )}
            <Typography
              variant="h5"
              component="div"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: changeColor }}
            >
              {scoreChange !== null ? `${Math.abs(scoreChange)} pts` : "—"}
            </Typography>
          </Box>
        </Box>

        {/* TODO: Items Completed — disabled until backend supports proper
            reporting of completed items (e.g. standards/action-plan tasks)
            with completion dates. Re-enable the block below and the
            `itemsCompleted` prop when available. */}
        {/* <Box>
          <Typography variant="caption" color="text.secondary">
            Items Completed
          </Typography>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 700, lineHeight: 1.2 }}
          >
            {itemsCompleted}
          </Typography>
        </Box> */}
      </Box>
    </Box>
  );
};

export const DashboardInsights = ({
  standardInsights,
  overallScore,
  benchmarks,
  verifiedScores,
}: StandardsGraphProps): React.ReactElement => {
  // const { submittedStandards, unsubmittedStandards } = useStandards();

  // const submittedScore = getStandardScore(submittedStandards);
  // const unsubmittedScore = getStandardScore(unsubmittedStandards);
  // const averageScore =
  //   (submittedScore + unsubmittedScore) /
  //   (submittedStandards.length + unsubmittedStandards.length);

  const scoreDataForPieChart = [
    { name: "Score", value: overallScore },
    { name: "Remaining", value: 100 - overallScore },
  ];

  interface CustomTickProps {
    x?: number;
    y?: number;
    payload?: {
      value: string;
    };
  }

  // map standard names to icon URLs
  const iconMap: Record<string, string> = {
    Hiring: hiringIcon,
    "Respect@Work": respectWorkIcon,
    "Performance Management": performanceManagementIcon,
    "Parental Leave": parentalLeaveIcon,
    "Transparent Pay": transparentPayIcon,
    Leadership: leadershipIcon,
    "Flexible Work": flexibleWorkIcon,
    Policies: policiesIcon,
    "Culture & Values": cultureValuesIcon,
    "SLT/Exec": sltExecIcon,
  };

  const renderCustomAxisTick = ({
    x = 0,
    y = 0,
    payload,
  }: CustomTickProps): JSX.Element => {
    if (!payload?.value) return <></>;

    const src = iconMap[payload?.value];
    if (!src) return <></>;

    // center the 24×24 icon on the tick
    const half = 12;
    return (
      <g transform={`translate(${x - half}, ${y + 4})`}>
        <title>{payload.value}</title>
        {/* Make the icon a link */}
        <a
          href={`${Paths.ACTION_PLAN_HOME_PAGE}/0`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ cursor: "pointer" }}
        >
          <image href={src} width={24} height={24} />
        </a>
      </g>
    );
  };

  const currentMaturityStage = maturityGraphStages.find(
    (x) => overallScore >= x.min && overallScore < x.max,
  )?.name;

  const scoreImage = (score: number) => {
    switch (true) {
      case score >= 80:
        return ImgAmazing;

      case score >= 70:
        return ImgGreatWork;

      case score >= 50:
        return ImgYouGotThis;

      default:
        return ImgKeepGoing;
    }
  };

  const latestVerified = verifiedScores.verifiedScore.length > 0
    ? verifiedScores.verifiedScore[verifiedScores.verifiedScore.length - 1]
    : null;
  const latestVerifiedScore = latestVerified ? latestVerified.score : null;

  // Only surface an unverified score once the user has actually updated an
  // action item since their last verification. With no prior verification there
  // is nothing to compare against, so the current score is inherently
  // unverified. Otherwise the score must have been recalculated for reasons
  // other than the user's action items, and we keep showing only the verified one.
  const hasUpdatesSinceLastVerified =
    latestVerified === null ||
    (verifiedScores.lastActionUpdatedDate !== null &&
      verifiedScores.lastActionUpdatedDate.getTime() >
        new Date(latestVerified.createdAt).getTime());

  const showUnverifiedOnChart =
    (latestVerifiedScore === null || overallScore !== latestVerifiedScore) &&
    hasUpdatesSinceLastVerified;

  const verifiedScoresOverTime = [
    ...verifiedScores.verifiedScore.map((s, index) => ({
      createdAt: s.createdAt.getTime(),
      score: s.score,
      unverifiedScore: undefined as number | undefined,
      connectingLine: (showUnverifiedOnChart && index === verifiedScores.verifiedScore.length - 1)
        ? s.score
        : undefined as number | undefined,
    })),
    ...(showUnverifiedOnChart
      ? [{
          createdAt: Date.now(),
          score: undefined as number | undefined,
          unverifiedScore: overallScore,
          connectingLine: overallScore,
        }]
      : []),
  ];

  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

  // The change is always the delta between the two most recent verified scores.
  // The earlier (baseline) score can be from any time — only the latest score's
  // date determines which window it falls into. e.g. 40 in May 2025 followed by
  // 50 in June 2026 is a +10 change in the last 6/12 months, not 0.
  const history = verifiedScores.verifiedScore;
  const latestScore = history[history.length - 1];
  const previousScore = history[history.length - 2];

  const latestChange =
    latestScore && previousScore
      ? latestScore.score - previousScore.score
      : null;

  const lastSixMonthsChange =
    latestScore && new Date(latestScore.createdAt) >= sixMonthsAgo
      ? latestChange
      : null;

  const lastTwelveMonthsChange =
    latestScore && new Date(latestScore.createdAt) >= twelveMonthsAgo
      ? latestChange
      : null;

  // TODO: "Items Completed" reporting is disabled for now. The only available
  // time-stamped data is the verified-score history, so this would just count
  // verified-score records per window — not a true count of completed items.
  // Proper reporting likely needs backend changes to expose completed items
  // (e.g. standards/action-plan tasks) with completion dates.
  // const itemsCompletedLastSixMonths = history.filter(
  //   (s) => new Date(s.createdAt) >= sixMonthsAgo,
  // ).length;
  // const itemsCompletedLastTwelveMonths = history.filter(
  //   (s) => new Date(s.createdAt) >= twelveMonthsAgo,
  // ).length;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h2"
          className="ap-home-page-heading margnT40"
          sx={{ paddingBottom: 3 }}
          style={{ marginTop: 0 }}
        >
          Your Dashboard
        </Typography>

        {/* Right-aligned group */}
        <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
          {/* <Stack direction="row" spacing={1}> */}
          <Typography
            variant="subtitle2"
            color="text.secondary"
            align="center"
            sx={{ fontSize: "0.7rem", fontWeight: 700 }}
          >
            Status
          </Typography>
          {verifiedScores.verifiedScore.length > 0 ? (
            <>
              <Chip
                icon={<StarIcon />}
                label="Verified"
                color="success"
                sx={{ backgroundColor: "#00c853", fontWeight: 700 }}
              />
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", marginBottom: "16px" }}
              >
                Last verified:{" "}
                {formatMonthYearAU(
                  verifiedScores.verifiedScore[
                    verifiedScores.verifiedScore.length - 1
                  ].createdAt,
                )}
              </Typography>
            </>
          ) : (
            <>
              <Chip
                icon={<CrossIcon />}
                label="Unverified"
                sx={{ fontWeight: 700 }}
              />
              {/* TODO: Calculate verification due date by user's sign up date / org sign up date ideally */}
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", marginBottom: "16px" }}
              >
                {/* Verification due: {
              date.format(new Date(verifiedScores.verifiedScore[verifiedScores.verifiedScore.length-1].createdAt), "MMMM YYYY")
              } */}
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Top Navigation tabs/links for future */}
        {/* <Grid item xs={12}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Button color="primary" variant="text">Overview</Button>
              <Button color="inherit">Detailed Benchmarks</Button>
              <Button color="inherit">Progress Over Time</Button>
              <Button color="inherit">Verification History</Button>
            </Grid>
            <Grid item>
              <IconButton color="primary"><AddIcon /></IconButton>
              <IconButton color="default"><EmojiObjectsIcon /></IconButton>
              <Avatar alt="" src="/static/images/avatar/1.jpg" />
            </Grid>
          </Grid>
        </Grid> */}

        {/* Card A: Maturity Level */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{
                  marginBottom: 4,
                }}
              >
                Maturity Level
                <InfoTooltip title="At-a-glance progress shown here on the gender DEI maturity scale, based on your completed self-assessments." />
              </Typography>

              <Box
                sx={{
                  margin: 3,
                }}
              >
                <Typography variant="h4" component="div">
                  {currentMaturityStage}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {
                    maturityList.find((x) => x.title === currentMaturityStage)
                      ?.description
                  }
                </Typography>
                <ResponsiveContainer width="100%" height={150}>
                  <MaturityGraph overallScore={overallScore} verifiedScores={verifiedScores.verifiedScore} showUnverified={showUnverifiedOnChart} />
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card C: Overall Score */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{
                  marginBottom: 4,
                }}
              >
                Overall Score
                <InfoTooltip title="At-a-glance progress shown here on the gender DEI maturity scale, based on your completed self-assessments." />
              </Typography>

              <Box
                sx={{
                  margin: 3,
                }}
                position="relative"
              >
                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 2 }}>
                  {verifiedScores.verifiedScore.length > 0 ? (
                    <>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Verified
                        </Typography>
                        <Typography variant="h4" component="div">
                          {verifiedScores.verifiedScore[verifiedScores.verifiedScore.length - 1].score}%
                        </Typography>
                      </Box>
                      {showUnverifiedOnChart && (
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary">
                            Unverified
                          </Typography>
                          <Typography variant="h4" component="div">
                            {overallScore}%
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Unverified
                      </Typography>
                      <Typography variant="h4" component="div" color="text.secondary">
                        {overallScore}%
                      </Typography>
                    </Box>
                  )}
                </Box>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={scoreDataForPieChart}
                      dataKey="value"
                      innerRadius={80}
                      outerRadius={100}
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={5}
                    >
                      {scoreDataForPieChart.map((_, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={
                            getPieChartColours(overallScore)[
                              index % PIE_CHART_COLORS.length
                            ]
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  component="img"
                  src={scoreImage(overallScore)}
                  alt="Center Logo"
                  sx={{
                    position: "absolute",
                    top: "60%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    zIndex: 0,
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card D: Bell Curve */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{ marginBottom: 4 }}
              >
                Benchmarks
                <InfoTooltip title="At-a-glance progress shown here on the gender DEI maturity scale, based on your completed self-assessments." />
              </Typography>

              <Benchmarking benchmarks={benchmarks} />
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Progress Over Time */}
        <Grid item xs={12} md={8} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{
                  marginBottom: 4,
                }}
              >
                Progress Over Time
                <InfoTooltip title="Score progression based on your verified assessments. The axis is zoomed in to highlight changes — full score range is 0–100%." />
              </Typography>

              <Box
                sx={{
                  margin: 3,
                }}
              >
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart
                  data={verifiedScoresOverTime}
                  // data={verifiedScores.verifiedScore}

                  margin={{ top: 16, right: 16, left: -16, bottom: 0 }}>

        <CartesianGrid vertical={false} stroke="rgba(136,135,128,0.15)" />
        {/* <XAxis
        dataKey="createdAt"
          scale="time"
          type="number"
          // domain={['dataMin', 'dataMax']}
          // ticks={verifiedScores.verifiedScore.map(v => new Date(v.createdAt).getTime())}
          // tickFormatter={(ts) => new Date(ts).toLocaleString('en-AU', { month: 'short', year: 'numeric' })}
          // axisLine={true} tickLine={false} tick={{ fontSize: 11 }}
        /> */}
        <XAxis
    dataKey="createdAt"
    type="number"
    scale="time"
    domain={['dataMin', 'dataMax']}
    // domain={[verifiedScores.verifiedScore[0]?.createdAt.getTime(), verifiedScores.verifiedScore[verifiedScores.verifiedScore.length - 1]?.createdAt.getTime()]}
    // ticks={verifiedScores.verifiedScore.map(d => d.createdAt.getTime())}
    tickFormatter={(ts) => new Date(ts).toLocaleString('en-AU', { month: 'short', year: 'numeric' })}
    axisLine={false}
    tickLine={false}
    tick={{ fontSize: 11 }}
  />
        <YAxis
          domain={verifiedScores.verifiedScore.length > 0 ? ["dataMin", "dataMax"] : [0, 100]}
          axisLine={false} tickLine={false} tick={{ fontSize: 11 }} tickCount={6}
          fontSize={24}
        />
        <Tooltip
          content={<ProgressOverTimeTooltip />}
          cursor={false}
        />
        <Line
          type="natural"
          dataKey="connectingLine"
          name="Projected"
          stroke="#d0d0d0"
          strokeWidth={1.5}
          dot={false}
          connectNulls={true}
          isAnimationActive={false}
        />
        <Line
          type="natural"
          dataKey="score"
          name="Verified score"
          stroke="#d0d0d0"
          strokeWidth={1.5}
          dot={{ r: 4, fill: 'white', stroke: '#faaa0a', strokeWidth: 2.5 }}
          connectNulls={true}
        />
        <Line
          type="natural"
          dataKey="unverifiedScore"
          name="Unverified score"
          stroke="none"
          strokeWidth={0}
          dot={{ r: 4, fill: 'white', stroke: '#9e9e9e', strokeWidth: 2.5 }}
          connectNulls={false}
          isAnimationActive={false}
        />
      </LineChart>
                </ResponsiveContainer>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
                  Axis scaled to show detail. Scores range from 0–100%. Progress is only stored and displayed for verified scores.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card: Score Changes */}
        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{ marginBottom: 2 }}
              >
                Recent Changes
                <InfoTooltip title="How your verified score has changed and how many assessments you've completed over recent time periods." />
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 4, mx: 3 }}>
                {/* Last 6 months */}
                <RecentChangePeriod
                  label="Last 6 months"
                  scoreChange={lastSixMonthsChange}
                  // TODO: re-enable once backend supports proper Items Completed reporting
                  // itemsCompleted={itemsCompletedLastSixMonths}
                />

                {/* Last 12 months */}
                <RecentChangePeriod
                  label="Last 12 months"
                  scoreChange={lastTwelveMonthsChange}
                  // TODO: re-enable once backend supports proper Items Completed reporting
                  // itemsCompleted={itemsCompletedLastTwelveMonths}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card B: Individual Standards */}
        <Grid item xs={12} md={8} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
                sx={{
                  marginBottom: 4,
                }}
              >
                Individual Standards
                <InfoTooltip title="At-a-glance progress shown here on the gender DEI maturity scale, based on your completed self-assessments." />
              </Typography>

              <Box
                sx={{
                  margin: 3,
                }}
              >
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={standardInsights}
                    margin={{ top: 30, right: 0, left: 0, bottom: 0 }}
                    barCategoryGap="20%"
                  >
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      fontSize={9}
                      height={60}
                      tick={renderCustomAxisTick}
                      interval={0}
                    />
                    <YAxis hide />
                    <Tooltip
                      cursor={false}
                      contentStyle={chartTooltipStyles.contentStyle}
                      labelStyle={chartTooltipStyles.labelStyle}
                      itemStyle={{ ...chartTooltipStyles.itemStyle, textTransform: "capitalize" }}
                    />
                    <Legend
                      payload={standardInsights.map((item) => ({
                        id: item.name,
                        type: "square",
                        value: item.name,
                        color: item.color,
                      }))}
                      formatter={(value) => (
                        <span style={{ color: "#000", fontSize: "10px" }}>
                          {value}
                        </span>
                      )}
                      layout="vertical"
                      align="right"
                      wrapperStyle={{ top: "-20px" }}
                    />
                    <Bar dataKey="score" radius={[6, 6, 0, 0]} barSize={16}>
                      <LabelList
                        dataKey="score"
                        position="top"
                        fill="#000000"
                        style={{ fontWeight: 600 }}
                        // formatter={(value: string | number) => `${value}%`}
                      />
                      {standardInsights.map((entry, index) => (
                        <Cell
                          name={entry.name}
                          key={`cell-${index}`}
                          fill={entry.color}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4} sx={{ display: "flex" }}>
          <Card className="insight-card width100" sx={{ flex: 1 }}>
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
              >
                Quick Links
              </Typography>
              <Grid item xs={12} sx={{ margin: 3 }}>
                <QuickLinksVertical />
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={12}>
          <Card className="insight-card width100">
            <CardContent>
              <Typography
                gutterBottom
                variant="h6"
                component="div"
                className="insight-card-heading"
              >
                Action Plans
              </Typography>
              <Grid item xs={12} sx={{ margin: 3 }}>
                <StandardsProgressGrid scores={standardInsights} />
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
